import {
  useMemo,
  useState,
  useContext,
  useCallback,
  createContext,
  type ReactNode,
} from 'react';

// ----------------------------------------------------------------------

type OpenOptions = {
  prefillScreeningCredits?: number;
  prefillInterviewCredits?: number;
  onPurchaseComplete?: () => void;
};

type CreditPurchaseContextValue = {
  isOpen: boolean;
  prefillScreeningCredits: number | null;
  prefillInterviewCredits: number | null;
  onPurchaseComplete: (() => void) | null;
  open: (opts?: OpenOptions) => void;
  close: () => void;
};

const CreditPurchaseContext = createContext<CreditPurchaseContextValue | null>(null);

// ----------------------------------------------------------------------

type CreditPurchaseProviderProps = {
  children: ReactNode;
};

/**
 * Hosts the open/close + prefill state for the global credit-purchase modal.
 * The `<CreditPurchaseModal />` component is mounted as a sibling in `app.tsx`
 * (not as a child here) so the provider stays a state container — the
 * provider→component cycle is intentionally avoided.
 */
export function CreditPurchaseProvider({ children }: CreditPurchaseProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [prefillScreeningCredits, setPrefillScreeningCredits] = useState<number | null>(null);
  const [prefillInterviewCredits, setPrefillInterviewCredits] = useState<number | null>(null);
  const [onPurchaseComplete, setOnPurchaseComplete] = useState<(() => void) | null>(null);

  const open = useCallback((opts?: OpenOptions) => {
    setPrefillScreeningCredits(opts?.prefillScreeningCredits ?? null);
    setPrefillInterviewCredits(opts?.prefillInterviewCredits ?? null);
    // Wrap in a function returner so useState's functional-setter contract isn't triggered
    setOnPurchaseComplete(() => opts?.onPurchaseComplete ?? null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setPrefillScreeningCredits(null);
    setPrefillInterviewCredits(null);
    setOnPurchaseComplete(null);
  }, []);

  const value = useMemo<CreditPurchaseContextValue>(
    () => ({
      isOpen,
      prefillScreeningCredits,
      prefillInterviewCredits,
      onPurchaseComplete,
      open,
      close,
    }),
    [isOpen, prefillScreeningCredits, prefillInterviewCredits, onPurchaseComplete, open, close]
  );

  return (
    <CreditPurchaseContext.Provider value={value}>
      {children}
    </CreditPurchaseContext.Provider>
  );
}

export function useCreditPurchase(): CreditPurchaseContextValue {
  const ctx = useContext(CreditPurchaseContext);
  if (!ctx) {
    throw new Error('useCreditPurchase must be used within a CreditPurchaseProvider');
  }
  return ctx;
}
